import React, { useState, useEffect } from 'react';
import { Grid2x2 as Grid, Home, Award, Phone, Mail, MapPin, Users, ChevronLeft, ChevronRight, X, Menu } from 'lucide-react';

export default function WelcomeScreen() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // All numbered pillow images from the public/pillows folder
  const pillowImages = [
    "/pillows/01.jpg",
    "/pillows/02.jpg",
    "/pillows/03.jpg",
    "/pillows/04.jpg",
    "/pillows/05.jpg",
    "/pillows/06.jpg",
    "/pillows/07.jpg",
    "/pillows/08.jpg",
    "/pillows/09.jpg",
    "/pillows/10.jpg",
    "/pillows/11.jpg",
    "/pillows/12.jpg",
    "/pillows/13.jpg",
    "/pillows/14.jpg",
    "/pillows/15.jpg",
    "/pillows/16.jpg"
  ];

  const testimonialImages = [
      {
      image: "/D.JPG",
      caption: "Thanks so much for making the beautiful pillow depicting our new home.  We have proudly displayed it in our living room.  It is such a gorgeous work of art.  Thank you again for such a lovely creation.  We will cherish it forever."
    },
    {
      image: "/A.jpeg",
      caption: "Absolutely speechless! Stunning pillow with amazing details! Mimi is great to work with and does a beautiful job! Very highly recommended!!!"
    },
    {
      image: "/C.JPG",
      caption: "Thank you for the beautiful pillow.  My daughters couldn't believe what an incredible job you did.  It is truly amazing."
    },
    {
      image: "/B.jpeg",
      caption: "We received your lovely pillow of our new home. It is sensational. We will enjoy it for many years to come."
    }
  ];

  // Email obfuscation function
  const getObfuscatedEmail = () => {
    const user = 'mimiiams';
    const domain = 'sbcglobal';
    const tld = 'net';
    return user + '@' + domain + '.' + tld;
  };

  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const email = getObfuscatedEmail();
    window.location.href = 'mailto:' + email;
  };


  const openImageModal = (index: number) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedImage === null) return;
    
    if (direction === 'prev') {
      setSelectedImage(selectedImage === 0 ? pillowImages.length - 1 : selectedImage - 1);
    } else {
      setSelectedImage(selectedImage === pillowImages.length - 1 ? 0 : selectedImage + 1);
    }
  };

  const handleModalBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the image container
    if (e.target === e.currentTarget) {
      closeImageModal();
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (selectedImage !== null) {
        if (e.key === 'ArrowLeft') {
          navigateImage('prev');
        } else if (e.key === 'ArrowRight') {
          navigateImage('next');
        }
      }
    };

    if (selectedImage !== null) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedImage]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50 to-amber-50">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-white z-50 transform transition-transform duration-300 ease-in-out translate-x-0">
          {/* Close Button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 p-2 text-slate-700 hover:text-emerald-600 transition-colors"
          >
            <X className="h-8 w-8" />
          </button>
          
          {/* Mobile Navigation */}
          <nav className="flex flex-col items-center justify-center h-full space-y-8 px-8">
            {/* Logo Section */}
            <div className="flex items-center space-x-3 mb-8">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-3 rounded-lg">
                <Home className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-skia text-slate-900">
                  <span className="inline-block mr-[-0.5rem]">MIMI</span> IAMS
                </h1>
                <p className="text-base text-slate-600 font-semibold">Fabric Artist</p>
              </div>
            </div>
            
            {/* Navigation Links */}
            <a 
              href="#about" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-3xl font-bold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              About
            </a>
            <a 
              href="#meetmimi" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-3xl font-bold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              Meet Mimi
            </a>
            <a 
              href="#gallery" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-3xl font-bold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              Gallery
            </a>
            <a 
              href="#testimonials" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-3xl font-bold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              Testimonials
            </a>
            <a 
              href="#contact" 
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-3xl font-bold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              Contact
            </a>
          </nav>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-2 rounded-lg">
                <Home className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-skia text-slate-700 hover:text-emerald-600 transition-colors">
                  <span className="inline-block mr-[-0.5rem]">MIMI</span> IAMS
                </h1>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">About</a>
              <a href="#meetmimi" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">Meet Mimi</a>
              <a href="#gallery" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">Gallery</a>
              <a href="#testimonials" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">Testimonials</a>
              <a href="#contact" className="text-slate-700 hover:text-emerald-600 transition-colors font-medium">Contact</a>
            </nav>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 text-slate-700 hover:text-emerald-600 transition-colors"
              >
                <Menu className="h-8 w-8" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-8 sm:gap-12">
            {/* Text Content */}
            <div className="flex-1 text-center sm:text-left">
              <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold font-skia mb-6">
                <Award className="h-4 w-4 mr-2" />
                Custom House Portrait Pillows
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Mimi creates <br />
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  Pillow Portraits
                </span>
                of your home
              </h2>

              <p className="text-base sm:text-md text-slate-700 leading-relaxed mb-6">
                Transform your home into a stunning keepsake. Each pillow is meticulously hand-embroidered with attention to architectural and landscaping details that make your home uniquely yours.
              </p>

             
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-8 sm:mb-0">
                {/* <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                  <span>Chicago North Shore</span>
                </div> */}
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-emerald-600" />
                  <span>(847) 251-4490</span>
                </div>
                {/* <div className="flex items-center">
                  <Award className="h-4 w-4 mr-2 text-emerald-600" />
                  <span>40+ Years Experience</span>
                </div> */}
              </div>
            </div>

            {/* Image */}
            <div className="relative flex-shrink-0 w-full sm:w-1/2 max-w-md sm:max-w-none">
              <img
                src="/pillows/Mimi.jpeg"
                alt="Mimi Iams - Custom House Portrait Pillow Artist"
                className="w-full max-w-sm lg:max-w-full mx-auto lg:mx-0 rounded-lg"
                width="400"
                height="400"
                loading="eager"
                fetchPriority="high"
                srcSet="/pillows/Mimi.jpeg 400w, /pillows/Mimi.jpeg 800w"
                sizes="(max-width: 640px) 400px, (max-width: 1024px) 400px, 800px"
              />
              <div className="absolute top-12 left-12 w-80 h-80 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full opacity-20 -z-10 animate-pulse"></div>
              <div className="absolute bottom-12 right-12 w-72 h-72 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full opacity-20 -z-10 animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section with Video */}
      <section id="about" className="bg-white py-12 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold font-skia mb-6">
            <Home className="h-4 w-4 mr-2" />
            About Mimi
          </div>
       
          <div className="max-w-4xl mx-auto">
            {/* About Content */}
            <div className="mb-12">
              <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">About The Artist</h3>
              
              <div className="space-y-6">
                <p className="text-lg text-slate-700 leading-relaxed">
                  Mimi Iams crafts fabric portraits of people's homes as keepsake pillows.
                </p>
                
                <p className="text-lg text-slate-700 leading-relaxed">
                  Mimi works from your photographs to render an architectural sketch, then cuts and assembles your home in velvets and other fabrics before hand and machine stitching the details.
                </p>
                
                <p className="text-lg text-slate-700 leading-relaxed">
                  Mimi has created well over twelve hundred of these wonderful keepsakes. She uses no assistants nor interns, insisting that the vision, the fun and the craftsmanship are hers alone.
                </p>
                
                <p className="text-lg text-slate-700 leading-relaxed">
                  Chicago's North Shore realtors have long used Mimi's pillows as impressive closing gifts. These pillows make the perfect heartfelt moment for someone moving into a new home or leaving a cherished family residence.
                </p>
              </div>
            </div>

            <div className="mb-12">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 shadow-xl">
                <div className="relative overflow-hidden shadow-2xl bg-black">
                  <div className="aspect-video">
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/THc97_GMkvs?modestbranding=1&rel=0&playsinline=1&cc_load_policy=1&iv_load_policy=3&fs=1&modestbranding=1"
                      title="Custom House Portrait Pillow Embroidery Process"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      sandbox="allow-scripts allow-same-origin allow-presentation"
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Second Video */}
            <div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 shadow-xl">
                <div className="relative overflow-hidden shadow-2xl bg-black">
                  <div className="aspect-video">
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/5YUaoBhBSsU?modestbranding=1&rel=0&playsinline=1&cc_load_policy=1&iv_load_policy=3&fs=1&modestbranding=1"
                      title="Custom House Portrait Pillow Embroidery Process"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      sandbox="allow-scripts allow-same-origin allow-presentation"
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work Gallery - 4x4 Grid */}
      <section id="gallery" className="bg-gradient-to-br from-slate-50 to-stone-100 py-8">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold font-skia mb-6">
            <Grid className="h-4 w-4 mr-2" />
            The Pillows
          </div>
          
          <div className="text-center mb-20">
            <h3 className="text-4xl font-bold text-slate-900 mb-6">Gallery</h3>
            <p className="text-xl text-slate-600">Click to enlarge any picture</p>
          </div>

          {/* 4x4 Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {pillowImages.map((image, index) => (
              <div
                key={index}
                className="aspect-square cursor-pointer group relative overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                onClick={() => openImageModal(index)}
              >
                <img
                  src={image}
                  alt={`Custom house portrait pillow ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  width="300"
                  height="300"
                  loading="lazy"
                  srcSet={`${image} 300w, ${image} 600w`}
                  sizes="(max-width: 768px) 300px, 300px"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3">
                    <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Image Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={handleModalBackdropClick}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            {/* Close Button */}
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors duration-300 z-10 cursor-pointer"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Navigation Arrows */}
            <button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/10 hover:bg-black/20 text-black p-4 rounded-full transition-all duration-300 z-10 cursor-pointer hover:scale-110"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
            
            <button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                navigateImage('next');
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/10 hover:bg-black/20 text-black p-4 rounded-full transition-all duration-300 z-10 cursor-pointer hover:scale-110"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
            
            {/* Modal Image */}
            <div className="relative bg-white overflow-hidden shadow-2xl cursor-default" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
              <img
                src={pillowImages[selectedImage]}
                alt={`Custom house portrait pillow ${selectedImage + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain"
                width="800"
                height="800"
                loading="eager"
                srcSet={`${pillowImages[selectedImage]} 800w, ${pillowImages[selectedImage]} 1200w`}
                sizes="(max-width: 1024px) 800px, 1200px"
              />
              
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {selectedImage + 1} of {pillowImages.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Testimonials Image Slideshow */}
      <section id="testimonials" className="bg-gradient-to-br from-slate-50 to-stone-100 py-12">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold font-skia mb-6">
            <Users className="h-4 w-4 mr-2" />
            The Recipients
          </div>
          
          <div className="text-center mb-20">
            <h3 className="text-4xl font-bold text-slate-900 mb-6">Delighted Clients</h3>
          </div>

          {/* 2x2 Grid of Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {testimonialImages.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                {/* Image Container */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={testimonial.image}
                    alt={`Delighted client ${index + 1} with their custom house portrait pillow`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    width="400"
                    height="300"
                    loading="lazy"
                    srcSet={`${testimonial.image} 400w, ${testimonial.image} 800w`}
                    sizes="(max-width: 768px) 400px, 400px"
                  />
                </div>
                
                {/* Caption Container */}
                <div className="p-8">
                  <blockquote className="text-slate-700 leading-relaxed italic text-center">
                    {testimonial.caption}
                  </blockquote>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-slate-900 mb-6">Easy to Order</h3>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="space-y-6">
              <a 
                href="tel:847-251-4490"
                className="flex items-center space-x-4 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 hover:from-emerald-100 hover:to-teal-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-lg group"
              >
                <div className="bg-emerald-600 p-3 rounded-full group-hover:scale-110 transition-transform duration-300">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h5 className="font-semibold text-slate-900 text-lg">Call Mimi</h5>
                  <span className="text-emerald-600 hover:text-emerald-700 font-semibold text-xl transition-colors">
                    (847) 251-4490
                  </span>
                  <p className="text-slate-600 text-sm mt-1">Pillows start at $450, depending on size and complexity.<br />  Oversize and double-sided pillows additional. <br /> Gift Certificates also available.</p>
                </div>
              </a>

              <button 
                onClick={handleEmailClick}
                className="w-full flex items-center space-x-4 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 hover:from-emerald-100 hover:to-teal-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-lg group cursor-pointer"
              >
                <div className="bg-emerald-600 p-3 rounded-full group-hover:scale-110 transition-transform duration-300">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h5 className="font-semibold text-slate-900 text-lg">Email Your Photos to</h5>
                  <span className="text-emerald-600 hover:text-emerald-700 font-semibold text-xl transition-colors">
                    {getObfuscatedEmail()}
                  </span>
                  <p className="text-slate-600 text-sm mt-1">Email Mimi photos of the house, including architectural details and landscaping. <br />She'll discuss all the choices with you, including background color and cording.</p>
                </div>
              </button>

              <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200">
                <div className="bg-emerald-600 p-3 rounded-full">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h5 className="font-semibold text-slate-900 text-lg">Based just north of Chicago</h5>
                  <p className="text-slate-900 text-sm mt-1">Mimi creates pillows for clients around the world</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Footer - Flipped Header Layout */}
      <footer className="bg-emerald-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <h1 className="text-4xl font-skia text-white">
                  <span className="inline-block mr-[-0.5rem]">MIMI</span> IAMS
                </h1>
                <p className="text-base text-emerald-100 font-semibold">Fabric Artist</p>
              </div>
              <div className="bg-emerald-700 p-3 rounded-lg">
                <Home className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}